import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { auth, db } from '../firebase';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import {
  onIdTokenChanged,
  signOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  signInWithCustomToken,
  User
} from 'firebase/auth';
import { initializeNewUser } from '../services/api';
import toast from 'react-hot-toast';

// API URL for backend calls
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// --- TYPE DEFINITIONS ---
interface UserProfile {
  handle?: string;
  displayName?: string;
  email?: string;
  photoURL?: string;
  ai_credits?: number;
  [key: string]: any;
}

interface TalkoruTokenInfo {
  status: 'existing_account' | 'new_account';
  email: string;
  display_name?: string;
  message: string;
}

interface AuthContextType {
  authLoading: boolean;
  isInitializing: boolean;
  currentUser: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  aiCredits: number;
  setAiCredits: (credits: number) => void;
  signUp: (email: string, password: string, displayName: string, handle?: string) => Promise<any>;
  logIn: (email: string, password: string) => Promise<any>;
  logout: () => Promise<void>;
  // Talkoru magic link states
  talkoruNeedsPassword: boolean;
  talkoruTokenInfo: TalkoruTokenInfo | null;
  talkoruProcessing: boolean;
  talkoruError: string | null;
  talkoruEmailEditable: boolean;
  completeTalkoruLogin: (password: string, emailOverride?: string) => Promise<boolean>;
  cancelTalkoruLogin: () => void;
}

interface AiLabContextType {
  analysisPair: any;
  setAnalysisPair: (pair: any) => void;
  aiAnalyses: Record<string, any>;
  setAnalysisForStrategy: (strategyId: string, analysis: any) => void;
  screenerParams: ScreenerParams;
  setScreenerParams: (params: ScreenerParams) => void;
  screenerResults: any;
  setScreenerResults: (results: any) => void;
  hasScreenerRun: boolean;
  setHasScreenerRun: (hasRun: boolean) => void;
}

interface ScreenerParams {
  universe: string;
  lookbackDays: number;
  alphaMetric: string;
  threshold: number;
}

// --- CONTEXT CREATION ---
const AuthContext = createContext<AuthContextType | undefined>(undefined);
const AiLabContext = createContext<AiLabContextType | undefined>(undefined);

// --- CUSTOM HOOKS ---
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function useAiLab(): AiLabContextType {
  const context = useContext(AiLabContext);
  if (!context) {
    throw new Error('useAiLab must be used within an AiLabProvider');
  }
  return context;
}

// --- AUTH PROVIDER ---
interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, _setLoading] = useState(true); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [authLoading, setAuthLoading] = useState(true);
  const [aiCredits, setAiCredits] = useState(0);

  // Talkoru magic link state
  const [talkoruNeedsPassword, setTalkoruNeedsPassword] = useState(false);
  const [talkoruTokenInfo, setTalkoruTokenInfo] = useState<TalkoruTokenInfo | null>(null);
  const [talkoruToken, setTalkoruToken] = useState<string | null>(null);
  const [talkoruProcessing, setTalkoruProcessing] = useState(false);
  const [talkoruError, setTalkoruError] = useState<string | null>(null);
  const [talkoruEmailEditable, setTalkoruEmailEditable] = useState(false);

  // Monitor auth state changes
  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, (user) => {
      setCurrentUser(user);
      if (!user) {
        setUserProfile(null);
        setAuthLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Handle talkoru_token in URL (user arriving from Talkoru) - secure flow with password
  useEffect(() => {
    const handleTalkoruToken = async () => {
      const params = new URLSearchParams(window.location.search);
      const urlToken = params.get('talkoru_token');

      if (!urlToken) return;

      console.log('Found talkoru_token, checking account status...');
      setTalkoruToken(urlToken);
      setTalkoruProcessing(true);
      setTalkoruError(null);

      try {
        // Step 1: Check if password is required
        const response = await fetch(`${API_URL}/api/bridge/check_token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: urlToken })
        });

        if (!response.ok) {
          const error = await response.json();
          console.error('Failed to check talkoru_token:', error);
          setTalkoruError(error.error || 'Token validation failed');
          // Clean URL on error
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete('talkoru_token');
          window.history.replaceState({}, '', newUrl.toString());
          return;
        }

        const data = await response.json();
        console.log('Token check result:', data);

        // Show password modal
        setTalkoruTokenInfo(data);
        setTalkoruNeedsPassword(true);
        // Show email input only if the token contains no email at all
        setTalkoruEmailEditable(!data.email);
      } catch (error: any) {
        console.error('Error handling talkoru_token:', error);
        setTalkoruError(error.message || 'Failed to process token');
      } finally {
        setTalkoruProcessing(false);
      }
    };

    handleTalkoruToken();
  }, []);

  // Complete Talkoru login with password
  const completeTalkoruLogin = async (password: string, emailOverride?: string): Promise<boolean> => {
    if (!talkoruToken || !talkoruTokenInfo) {
      setTalkoruError('No token available');
      return false;
    }

    setTalkoruProcessing(true);
    setTalkoruError(null);

    try {
      const isNewAccount = talkoruTokenInfo.status === 'new_account';
      const response = await fetch(`${API_URL}/api/bridge/generate_firebase_token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: talkoruToken,
          password,
          is_new_account: isNewAccount,
          ...(emailOverride ? { email_override: emailOverride } : {})
        })
      });

      if (!response.ok) {
        const error = await response.json();
        setTalkoruError(error.error || 'Login failed');
        return false;
      }

      const data = await response.json();
      if (data.firebase_token) {
        console.log('Signing in with custom token from Talkoru...');
        await signInWithCustomToken(auth, data.firebase_token);

        // Clean URL
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('talkoru_token');
        window.history.replaceState({}, '', newUrl.toString());

        // Reset state
        setTalkoruNeedsPassword(false);
        setTalkoruTokenInfo(null);
        setTalkoruToken(null);
        setTalkoruEmailEditable(false);

        toast.success('Welcome to AILabQuant!');
        return true;
      }
      return false;
    } catch (error: any) {
      console.error('Error completing Talkoru login:', error);
      setTalkoruError(error.message || 'Login failed');
      return false;
    } finally {
      setTalkoruProcessing(false);
    }
  };

  // Cancel Talkoru login
  const cancelTalkoruLogin = () => {
    // Clean URL
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.delete('talkoru_token');
    window.history.replaceState({}, '', newUrl.toString());

    // Reset state
    setTalkoruNeedsPassword(false);
    setTalkoruTokenInfo(null);
    setTalkoruToken(null);
    setTalkoruEmailEditable(false);
    setTalkoruError(null);
  };

  // Fetch user profile from Firestore
  useEffect(() => {
    const fetchUserProfile = async (user: User) => {
      try {
        const userDocRef = doc(db, `artifacts/ailabquant/users/${user.uid}`);
        const docSnap = await getDoc(userDocRef);

        if (docSnap.exists()) {
          setUserProfile(docSnap.data() as UserProfile);
        } else {
          console.warn("AuthContext: User document not found in Firestore for UID:", user.uid);
          setUserProfile(null);
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
        setUserProfile(null);
      } finally {
        setAuthLoading(false);
      }
    };

    if (currentUser) {
      fetchUserProfile(currentUser);
    }
  }, [currentUser]);

  // Real-time sync of AI credits
  useEffect(() => {
    if (authLoading || !currentUser) {
      setAiCredits(0);
      return;
    }

    const userDocRef = doc(db, `artifacts/ailabquant/users/${currentUser.uid}`);
    const unsubscribeFromSnapshot = onSnapshot(
      userDocRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          setAiCredits(docSnapshot.data().ai_credits ?? 0);
        }
      },
      (error) => {
        console.error("Firestore snapshot error:", error);
      }
    );

    return () => {
      console.log("AuthContext: Cleaning up session for user", currentUser.uid);
      unsubscribeFromSnapshot();
    };
  }, [currentUser, authLoading]);

  // Auth methods
  async function signUp(email: string, password: string, displayName: string, handle?: string) {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(userCredential.user, { displayName });
    const token = await userCredential.user.getIdToken();
    
    // Auto-generate handle from displayName if not provided
    const userHandle = handle || displayName.toLowerCase().replace(/\s+/g, '') + Math.floor(Math.random() * 1000);
    await initializeNewUser(token, displayName, userHandle);
    return userCredential;
  }

  function logIn(email: string, password: string) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  function logout() {
    return signOut(auth);
  }

  const value: AuthContextType = {
    authLoading,
    isInitializing: authLoading,
    currentUser,
    userProfile,
    loading,
    aiCredits,
    setAiCredits,
    signUp,
    logIn,
    logout,
    // Talkoru magic link
    talkoruNeedsPassword,
    talkoruTokenInfo,
    talkoruProcessing,
    talkoruError,
    talkoruEmailEditable,
    completeTalkoruLogin,
    cancelTalkoruLogin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// --- AI LAB PROVIDER ---
export function AiLabProvider({ children }: AuthProviderProps) {
  const [analysisPair, setAnalysisPair] = useState<any>(null);
  const [aiAnalyses, setAiAnalyses] = useState<Record<string, any>>({});
  const [screenerParams, setScreenerParams] = useState<ScreenerParams>({
    universe: 'SP100',
    lookbackDays: 30,
    alphaMetric: 'alpha_score',
    threshold: 0.1,
  });
  const [screenerResults, setScreenerResults] = useState<any>(null);
  const [hasScreenerRun, setHasScreenerRun] = useState(false);

  const setAnalysisForStrategy = (strategyId: string, analysis: any) => {
    setAiAnalyses((prev) => ({ ...prev, [strategyId]: analysis }));
  };

  const value: AiLabContextType = {
    analysisPair,
    setAnalysisPair,
    aiAnalyses,
    setAnalysisForStrategy,
    screenerParams,
    setScreenerParams,
    screenerResults,
    setScreenerResults,
    hasScreenerRun,
    setHasScreenerRun,
  };

  return <AiLabContext.Provider value={value}>{children}</AiLabContext.Provider>;
}

